import { ContractModelTypes } from '@mono/proto-clients';
import { useQuery } from '@tanstack/react-query';
import { contractModelClient, deliveryClient } from '../../../lib/connect/clients';
import { addDays } from '../Deliverables/lib/format';
// Phase 5c — the delivery calendar reads cross-project deadlines (PoP
// option-period expirations, protest/debrief windows) that the background
// syncer materializes into delivery.calendar_events. ryugu projects
// the rows PER PROGRAM, so a NoView program's events are dropped and disclosed
// as hidden_count. Materialization is a background/admin op (ryugu deliberately
// does not expose a browser sync RPC) — the UI reads and refetches only.
const KEY = 'delivery-calendar';
export function useDeliveryCalendar(filter) {
    return useQuery({
        queryKey: [
            KEY,
            filter
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.getDeliveryCalendar({
                companyId: filter.companyId,
                eventType: filter.eventType ?? ''
            });
            return {
                // Deliverable due dates are the contract model's (below); the old
                // model's deliverable_due rows are dropped so no date shows twice.
                events: resp.events.filter((e)=>e.eventType !== 'deliverable_due'),
                // DRIFT-2: NoView programs' events are dropped server-side; surface how
                // many so deadlines for hidden programs are disclosed, never silent.
                hiddenCount: resp.hiddenCount
            };
        },
        enabled: !!filter.companyId,
        staleTime: 30000
    });
}
// ---------------------------------------------------------------------------
// Deliverable due dates (contract model v2, D113): one ListOccurrences per
// program over the visible month, folded here.
// ---------------------------------------------------------------------------
/** "2026-09" → the month's first and last calendar day. */ export function monthSpan(month) {
    const [y, m] = month.split('-').map(Number);
    const from = `${month}-01`;
    const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
    return {
        from,
        to: addDays(next, -1)
    };
}
/** "2026-09" shifted by n months. */ export function shiftMonth(month, n) {
    const [y, m] = month.split('-').map(Number);
    const t = y * 12 + (m - 1) + n;
    return `${Math.floor(t / 12)}-${String(t % 12 + 1).padStart(2, '0')}`;
}
/**
 * Every visible program's deliverable dates due in the month, in one
 * ListPortfolioOccurrences (SAD-1000): the rows each program's
 * ListOccurrences lists, already joined to their deliverable's title and
 * final tier. It used to be one ListOccurrences and one ListDuties per
 * program.
 */ export function useDeliverableDueDates(programIds, month) {
    const { from, to } = monthSpan(month);
    const q = useQuery({
        queryKey: [
            KEY,
            'deliverables',
            programIds.join(','),
            from,
            to
        ],
        enabled: programIds.length > 0,
        staleTime: 30000,
        queryFn: async ()=>{
            const resp = await contractModelClient.listPortfolioOccurrences({
                body: new ContractModelTypes.ListPortfolioOccurrencesRequest({
                    filter: new ContractModelTypes.GetPortfolioDeliverySummaryRequest({
                        programIds,
                        window: ContractModelTypes.OccurrenceWindow.ALL,
                        dueFrom: from,
                        dueTo: to
                    }),
                    pageSize: 0
                })
            });
            return new ContractModelTypes.ListPortfolioOccurrencesResponse(resp.body ?? {});
        }
    });
    const dates = (q.data?.rows ?? []).flatMap((r)=>r.occurrence ? [
            {
                programId: r.programId,
                programName: r.programName,
                occurrence: r.occurrence,
                title: r.dutyTitle,
                finalReviewTier: r.finalReviewTier
            }
        ] : []);
    return {
        dates,
        isLoading: q.isLoading,
        // One read for every program: when it fails, every program's dates are missing.
        failed: q.isError ? programIds.length : 0,
        isFetching: q.isFetching,
        refetch: ()=>{
            void q.refetch();
        }
    };
}
